import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { confirmDialog } from "primereact/confirmdialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";

import { LoadingSpinner } from "../../components/LoadingSpinner";
import {
  useCloseSupportChatMutation,
  useGetChatQuery,
  useSendSupportMessageMutation,
  useTakeOverChatMutation,
} from "../../store/queries/chat";
import { IChatMessage } from "../../types/Chat";
import {
  canEndChat,
  canReply,
  canTakeOver,
  getAdminPollingInterval,
  getStaffUserName,
  getTranscriptSenderLabel,
  isSafeHttpUrl,
  SUPPORT_STATUS_LABELS,
  SUPPORT_STATUS_SEVERITY,
  supportStatusOf,
} from "./utils/chatSupportAdmin.helpers";

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`;

const HeaderCard = styled(Card)`
  margin-bottom: 24px;
`;

const MessagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 600px;
  overflow-y: auto;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const MessageBubble = styled.div<{ isUser: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.isUser ? "flex-end" : "flex-start")};
  margin-bottom: 12px;
`;

const MessageContent = styled.div<{ isUser: boolean; isSupport?: boolean }>`
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 18px;
  background-color: ${(props) => {
    if (props.isUser) return "#007bff";
    if (props.isSupport) return "#eef4ff";
    return "#e9ecef";
  }};
  border: ${(props) => (props.isSupport ? "1px solid #6c63ff" : "none")};
  color: ${(props) => (props.isUser ? "white" : "#333")};
  word-wrap: break-word;
  white-space: pre-wrap;
`;

/** CLE-3043 — "Sent to Clear Health support", "<Name> joined", "Chat ended…". */
const SystemLine = styled.div`
  align-self: center;
  font-size: 0.8rem;
  color: #6c757d;
  text-align: center;
`;

const SupportActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

const ReplyBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const MessageMeta = styled.div<{ isUser: boolean }>`
  font-size: 0.75rem;
  color: #6c757d;
  margin-top: 4px;
  text-align: ${(props) => (props.isUser ? "right" : "left")};
`;

const SenderName = styled.span`
  font-weight: 600;
  margin-right: 8px;
`;

const BackButton = styled(Button)`
  margin-bottom: 16px;
`;

const ChatInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoLabel = styled.span`
  font-size: 0.875rem;
  color: #6c757d;
  font-weight: 600;
`;

const InfoValue = styled.span`
  font-size: 1rem;
  color: #333;
`;

const LinkText = styled.span`
  color: #0066cc;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

function ChatViewPage() {
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();
  const toast = useContext(ToastContext);
  const [reply, setReply] = useState("");
  // CLE-3043 — poll only while support is involved; the status comes from the last answer.
  const [pollingInterval, setPollingInterval] = useState(0);
  const {
    data: chat,
    isLoading,
    error,
  } = useGetChatQuery(chatId || "", {
    skip: !chatId,
    pollingInterval,
  });
  const supportStatus = supportStatusOf(chat);
  useEffect(() => {
    setPollingInterval(getAdminPollingInterval(supportStatus));
  }, [supportStatus]);

  const [takeOverChat, { isLoading: isTakingOver }] = useTakeOverChatMutation();
  const [sendSupportMessage, { isLoading: isReplying }] =
    useSendSupportMessageMutation();
  const [closeSupportChat, { isLoading: isClosing }] =
    useCloseSupportChatMutation();

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const showError = (summary: string, err: any) => {
    toast?.current?.show({
      severity: "error",
      summary,
      detail:
        typeof err?.data?.data === "string"
          ? err.data.data
          : "Please try again.",
      life: 5000,
    });
  };

  const handleTakeOver = async () => {
    if (!chatId) return;
    try {
      await takeOverChat(chatId).unwrap();
    } catch (err) {
      showError("Could not take over this chat", err);
    }
  };

  const handleReply = async () => {
    const message = reply.trim();
    if (!chatId || !message) return;
    try {
      await sendSupportMessage({ chatId, message }).unwrap();
      setReply("");
    } catch (err) {
      showError("Reply not sent", err);
    }
  };

  const handleEndChat = () => {
    if (!chatId) return;
    confirmDialog({
      header: "End this chat?",
      message:
        "The hospital staff member will see that the chat has ended and will need to start a new one. This cannot be undone.",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "End chat",
      rejectLabel: "Cancel",
      accept: async () => {
        try {
          await closeSupportChat(chatId).unwrap();
        } catch (err) {
          showError("Could not end this chat", err);
        }
      },
    });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !chat) {
    return (
      <Container>
        <Card>
          <h2>Error</h2>
          <p>Chat not found or failed to load.</p>
          <Button
            label="Back to Chats"
            icon="pi pi-arrow-left"
            onClick={() => navigate("/chatbot/chats")}
          />
        </Card>
      </Container>
    );
  }

  const messages = (chat.chatMessages || []) as IChatMessage[];
  const chatType = chat.chatModel?.type;
  const isStaffChat = chatType === "ContractManagementBot";
  const staffName = getStaffUserName(chat);

  return (
    <Container>
      <BackButton
        label="Back to Chats"
        icon="pi pi-arrow-left"
        className="p-button-text p-button-secondary"
        onClick={() => navigate("/chatbot/chats")}
      />

      <HeaderCard>
        <h1>Chat Conversation</h1>
        <ChatInfo>
          <InfoItem>
            <InfoLabel>Chat ID:</InfoLabel>
            <InfoValue>{chat.id}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Type:</InfoLabel>
            <InfoValue>
              {isStaffChat ? "Hospital staff" : "Patient"} ({chatType})
            </InfoValue>
          </InfoItem>
          {isStaffChat && (
            <InfoItem>
              <InfoLabel>Staff user:</InfoLabel>
              <InfoValue>
                {staffName ?? "Unknown"}
                {chat.user?.email && staffName !== chat.user.email
                  ? ` (${chat.user.email})`
                  : ""}
              </InfoValue>
            </InfoItem>
          )}
          {isStaffChat && (
            <InfoItem>
              <InfoLabel>Support:</InfoLabel>
              <InfoValue>
                <Tag
                  value={SUPPORT_STATUS_LABELS[supportStatus]}
                  severity={SUPPORT_STATUS_SEVERITY[supportStatus]}
                />
                {chat.supportAgentName ? ` ${chat.supportAgentName}` : ""}
              </InfoValue>
            </InfoItem>
          )}
          {isStaffChat && chat.escalationPageUrl && (
            <InfoItem>
              <InfoLabel>Escalated from:</InfoLabel>
              <InfoValue>
                {isSafeHttpUrl(chat.escalationPageUrl) ? (
                  <a
                    href={chat.escalationPageUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {chat.escalatedAt
                      ? formatTimestamp(chat.escalatedAt)
                      : "Page"}
                  </a>
                ) : (
                  <span>
                    {chat.escalatedAt
                      ? formatTimestamp(chat.escalatedAt)
                      : "Page"}
                  </span>
                )}
              </InfoValue>
            </InfoItem>
          )}
          <InfoItem>
            <InfoLabel>Account:</InfoLabel>
            <InfoValue>
              <LinkText
                onClick={() => navigate(`/hospitals/view/${chat.accountId}`)}
              >
                {chat.account.name}
              </LinkText>
            </InfoValue>
          </InfoItem>
          {chat.data?.orderId && (
            <InfoItem>
              <InfoLabel>Estimate:</InfoLabel>
              <InfoValue>
                <LinkText
                  onClick={() =>
                    chat.data?.orderId &&
                    navigate(`/orders/${chat.data.orderId}`)
                  }
                >
                  {chat.data.orderId}
                </LinkText>
              </InfoValue>
            </InfoItem>
          )}
          <InfoItem>
            <InfoLabel>Model:</InfoLabel>
            <InfoValue>
              <LinkText
                onClick={() => navigate(`/chatbot/models/${chat.chatModelId}`)}
              >
                {chat.chatModel.name}
              </LinkText>
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Started:</InfoLabel>
            <InfoValue>{formatTimestamp(chat.createdAt)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Last Updated:</InfoLabel>
            <InfoValue>{formatTimestamp(chat.updatedAt)}</InfoValue>
          </InfoItem>
        </ChatInfo>
        {isStaffChat && (
          <SupportActions>
            {/* Take over: WAITING only — a chat the staff member has not sent to support stays the bot's. */}
            <Button
              label="Take over"
              icon="pi pi-user"
              onClick={handleTakeOver}
              disabled={!canTakeOver(supportStatus) || isTakingOver}
              loading={isTakingOver}
            />
            <Button
              label="End chat"
              icon="pi pi-times"
              className="p-button-danger p-button-outlined"
              onClick={handleEndChat}
              disabled={!canEndChat(supportStatus) || isClosing}
              loading={isClosing}
            />
          </SupportActions>
        )}
      </HeaderCard>

      <Card title={`Messages (${messages.length})`}>
        {messages.length > 0 ? (
          <MessagesContainer>
            {messages.map((message, index) => {
              const isUser = message.role === "user";
              const sender = getTranscriptSenderLabel(
                message,
                chatType,
                staffName
              );
              if (sender === null) {
                return (
                  <SystemLine key={index}>
                    {message.content} · {formatTimestamp(message.timestamp)}
                  </SystemLine>
                );
              }
              return (
                <MessageBubble key={index} isUser={isUser}>
                  <MessageContent
                    isUser={isUser}
                    isSupport={isStaffChat && message.author === "support"}
                  >
                    {message.content}
                  </MessageContent>
                  <MessageMeta isUser={isUser}>
                    <SenderName>{sender}</SenderName>
                    {formatTimestamp(message.timestamp)}
                  </MessageMeta>
                </MessageBubble>
              );
            })}
          </MessagesContainer>
        ) : (
          <p className="text-muted">No messages in this conversation yet.</p>
        )}
        {isStaffChat && canReply(supportStatus) && (
          <ReplyBox>
            <InputTextarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={3}
              autoResize
              placeholder={`Reply to ${
                staffName ?? "the hospital staff member"
              }…`}
              disabled={isReplying}
            />
            <div>
              <Button
                label="Send reply"
                icon="pi pi-send"
                onClick={handleReply}
                disabled={!reply.trim() || isReplying}
                loading={isReplying}
              />
            </div>
          </ReplyBox>
        )}
      </Card>
    </Container>
  );
}

export default ChatViewPage;
